/**
 * CitationTooltip — floating tooltip for citation decorations in the TipTap editor.
 *
 * Renders a positioned popover showing document name, page number, and a
 * "View in document" button. Positioned relative to the clicked citation
 * element using a React portal.
 */

import { useEffect, useRef, useCallback } from 'react'
import { FileText, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePreviewStore } from '@/stores/preview-store'
import { displayCitationPage, openCitationSource } from '@/lib/citation-preview'
import type { CitationRef } from '@/lib/api'

// ===================
// Types
// ===================

export interface CitationTooltipState {
  citationIndex: number
  sectionId: string
  citationType: 'tender' | 'library'
  anchorRect: DOMRect
}

interface CitationTooltipProps {
  state: CitationTooltipState | null
  onClose: () => void
  /** Map of sectionId → citationRefs for lookup */
  citationRefsBySectionId: Map<string, CitationRef[]>
  /** Fallback citationRefs when there's only one section (no segmentHeadings) */
  fallbackCitationRefs?: CitationRef[]
  /** Map of sectionId → libraryCitationRefs for lookup */
  libraryCitationRefsBySectionId?: Map<string, CitationRef[]>
  /** Fallback library citationRefs when there's only one section */
  fallbackLibraryCitationRefs?: CitationRef[]
}

// ===================
// Component
// ===================

export function CitationTooltip({
  state,
  onClose,
  citationRefsBySectionId,
  fallbackCitationRefs,
  libraryCitationRefsBySectionId,
  fallbackLibraryCitationRefs,
}: CitationTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const openPreview = usePreviewStore((s) => s.openPreview)

  // Close on click outside
  useEffect(() => {
    if (!state) return

    function handleClickOutside(e: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    // Close on Escape
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [state, onClose])

  const handleViewInDocument = useCallback(() => {
    if (!state) return

    const isLibrary = state.citationType === 'library'
    const refs = isLibrary
      ? (libraryCitationRefsBySectionId?.get(state.sectionId) ?? fallbackLibraryCitationRefs ?? [])
      : (citationRefsBySectionId.get(state.sectionId) ?? fallbackCitationRefs ?? [])
    const ref = refs.find((r) => r.citationIndex === state.citationIndex)
    if (!ref) return

    openCitationSource(ref, isLibrary, openPreview)

    onClose()
  }, [state, citationRefsBySectionId, fallbackCitationRefs, libraryCitationRefsBySectionId, fallbackLibraryCitationRefs, openPreview, onClose])

  if (!state) return null

  // Resolve citation data
  const isLibrary = state.citationType === 'library'
  const refs = isLibrary
    ? (libraryCitationRefsBySectionId?.get(state.sectionId) ?? fallbackLibraryCitationRefs ?? [])
    : (citationRefsBySectionId.get(state.sectionId) ?? fallbackCitationRefs ?? [])
  const ref = refs.find((r) => r.citationIndex === state.citationIndex)
  const shownPage = displayCitationPage(ref?.pageNumber)

  // Position tooltip above the citation
  const top = state.anchorRect.top - 8
  const left = state.anchorRect.left

  return (
    <div
      ref={tooltipRef}
      className="fixed z-50 -translate-y-full"
      style={{ top, left }}
    >
      <div className="bg-card border shadow-lg rounded-lg p-3 space-y-2 max-w-xs">
        {ref ? (
          <>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium truncate">
                {ref.documentName}
              </span>
            </div>

            {shownPage != null && (
              <div className="text-meta">
                Page {shownPage}
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={handleViewInDocument}
            >
              <ExternalLink className="h-3 w-3 mr-1.5" />
              View in document
            </Button>
          </>
        ) : (
          <div className="text-meta">
            Citation [{isLibrary ? `L${state.citationIndex}` : state.citationIndex}] — source not available
          </div>
        )}
      </div>
    </div>
  )
}
